import { create } from 'zustand';
import type { MediaParentEntity } from '@/types/dtos/media/media-parent-entity.enum.ts';
import type {
  FlowCueStateV1,
  LiveContextPerformControlWsPayloadV1,
  LiveCueSheetV1,
  MediaAgentRecoveryV1,
  PerformActionItemState,
  PerformControlV1,
} from '@/types/player/perform-control-v1.types.ts';
import { mergeCueStateFromGetAndWs } from '@/helpers/perform-staging.helper';
import { isMediaAgentRecoveryEngaged } from '@/helpers/media-agent-recovery.helper';
import { useLiveSequenceIndexStore } from '@/state/stores/use-live-sequence-index-store';
import { useLocalMediaPreviewSlideStore } from '@/state/stores/use-local-media-preview-slide-store';

export type { LiveContextPerformControlWsPayloadV1 } from '@/types/player/perform-control-v1.types.ts';

type EntityRoomKey = `${MediaParentEntity}::${string}`;

export function entityRoomKeyFromEntityRef(entityRef: string): EntityRoomKey | null {
  const idx = entityRef.indexOf(':');
  if (idx <= 0 || idx >= entityRef.length - 1) {
    return null;
  }
  const entityType = entityRef.slice(0, idx) as MediaParentEntity;
  const entityId = entityRef.slice(idx + 1);
  if (entityType !== 'Flow' && entityType !== 'Event') {
    return null;
  }
  return `${entityType}::${entityId}`;
}

type PerformControlWsSlice = {
  lastSeqByRoom: Record<string, number>;
  aggregateByRoom: Record<string, PerformActionItemState>;
  itemsByRoom: Record<string, Record<string, PerformActionItemState>>;
  cueStateByRoom: Record<string, FlowCueStateV1>;
  /**
   * After a post-command GET replace, ignore pointer-based merge until a perform-control payload
   * that includes `cueState` arrives. Prevents a stale higher-pointer WS overlay from winning a
   * backward jump (seq can advance on non-cue milestones without refreshing cueState).
   */
  cueGetAuthorityUntilSeqByRoom: Record<string, number>;
  /** Latest media-agent outage episode per room; absent until the service emits one. */
  mediaAgentRecoveryByRoom: Record<string, MediaAgentRecoveryV1>;
  /** Live cue sheet per room (Event and Flow). Apply is revision-monotonic. */
  cueSheetByRoom: Record<string, LiveCueSheetV1>;
  /** @returns true when the payload was applied (seq advanced). */
  applyPayload: (roomKey: EntityRoomKey, payload: LiveContextPerformControlWsPayloadV1) => boolean;
  /**
   * GET hydrate: replace aggregate + items from REST; replace cueState only when GET includes it.
   *
   * **Seq rule (stale WS after refetch):** we intentionally do **not** reset `lastSeqByRoom` here.
   * Server `seq` is monotonic per `entityRef`. After refetch, replacing items/aggregate/cueState from GET
   * aligns baseline with the DB; any in-flight WebSocket with `seq <= lastSeq` is ignored by
   * `applyPayload`, and only strictly newer `seq` can patch on top — so a delayed **older** seq
   * cannot overwrite fresh GET state.
   *
   * **cueState omit:** a GET without `cueState` must not wipe a fresher WS overlay (mid-play race).
   */
  hydrateFromGet: (
    roomKey: EntityRoomKey,
    performControl: PerformControlV1,
    cueState?: FlowCueStateV1,
    cueSheet?: LiveCueSheetV1,
    mediaSequenceIndex?: Record<string, number>,
    mediaAgentRecovery?: MediaAgentRecoveryV1,
  ) => void;
  /**
   * Post-cue-command GET is authoritative: replace the room cue overlay and latch merge authority
   * until a newer WS seq lands (so backward jumps cannot keep a higher pre-jump pointer).
   */
  replaceCueStateFromGet: (roomKey: EntityRoomKey, cueState: FlowCueStateV1) => void;
  resetRoom: (roomKey: EntityRoomKey) => void;
};

export const usePerformControlWsStore = create<PerformControlWsSlice>((set, get) => ({
  lastSeqByRoom: {},
  aggregateByRoom: {},
  itemsByRoom: {},
  cueStateByRoom: {},
  cueGetAuthorityUntilSeqByRoom: {},
  mediaAgentRecoveryByRoom: {},
  cueSheetByRoom: {},
  applyPayload: (roomKey, payload) => {
    if (payload.version !== 1 || typeof payload.seq !== 'number') {
      return false;
    }
    const last = get().lastSeqByRoom[roomKey] ?? 0;
    if (payload.seq <= last) {
      return false;
    }
    const prevItems = get().itemsByRoom[roomKey] ?? {};
    let nextItems: Record<string, PerformActionItemState>;
    if (payload.items) {
      nextItems = { ...payload.items };
    } else if (payload.changedItems) {
      nextItems = { ...prevItems };
      for (const [k, v] of Object.entries(payload.changedItems)) {
        if (v !== undefined) {
          nextItems[k] = v;
        }
      }
    } else {
      nextItems = { ...prevItems };
    }
    const nextCueStateByRoom = { ...get().cueStateByRoom };
    const nextAuthority = { ...get().cueGetAuthorityUntilSeqByRoom };
    if (payload.cueState) {
      nextCueStateByRoom[roomKey] = payload.cueState;
      // Fresher WS cue snapshot supersedes post-command GET authority.
      delete nextAuthority[roomKey];
    }
    // Absent = no phase change in this payload; keep the current episode (also carries `active: false` clears).
    const nextRecoveryByRoom = { ...get().mediaAgentRecoveryByRoom };
    if (payload.mediaAgentRecovery) {
      nextRecoveryByRoom[roomKey] = payload.mediaAgentRecovery;
    }
    const nextCueSheetByRoom = { ...get().cueSheetByRoom };
    if (payload.cueSheet) {
      const existingSheet = nextCueSheetByRoom[roomKey];
      if (!existingSheet || payload.cueSheet.revision >= existingSheet.revision) {
        nextCueSheetByRoom[roomKey] = payload.cueSheet;
      }
    }
    set({
      lastSeqByRoom: { ...get().lastSeqByRoom, [roomKey]: payload.seq },
      aggregateByRoom: { ...get().aggregateByRoom, [roomKey]: payload.aggregate },
      itemsByRoom: { ...get().itemsByRoom, [roomKey]: nextItems },
      cueStateByRoom: nextCueStateByRoom,
      cueGetAuthorityUntilSeqByRoom: nextAuthority,
      mediaAgentRecoveryByRoom: nextRecoveryByRoom,
      cueSheetByRoom: nextCueSheetByRoom,
    });
    return true;
  },
  hydrateFromGet: (roomKey, performControl, cueState, cueSheet, mediaSequenceIndex, mediaAgentRecovery) => {
    if (performControl.version !== 1) return;
    const nextCueStateByRoom = { ...get().cueStateByRoom };
    if (cueState) {
      if (get().cueGetAuthorityUntilSeqByRoom[roomKey] !== undefined) {
        // Post-command GET window: do not re-merge a stale higher-pointer WS overlay.
        nextCueStateByRoom[roomKey] = cueState;
      } else {
        const existing = get().cueStateByRoom[roomKey];
        // Merge so a late GET cannot roll WS on_air / pointer back to a stale preloading snapshot.
        nextCueStateByRoom[roomKey] = mergeCueStateFromGetAndWs(cueState, existing) ?? cueState;
      }
    }
    const nextCueSheetByRoom = { ...get().cueSheetByRoom };
    if (cueSheet) {
      const existingSheet = nextCueSheetByRoom[roomKey];
      if (!existingSheet || cueSheet.revision >= existingSheet.revision) {
        nextCueSheetByRoom[roomKey] = cueSheet;
      }
    }
    // else: keep existing WS/GET cue overlay — do not delete on partial snapshots
    const nextRecoveryByRoom = { ...get().mediaAgentRecoveryByRoom };
    if (mediaAgentRecovery) {
      const existing = nextRecoveryByRoom[roomKey];
      const existingCleared = existing != null && !isMediaAgentRecoveryEngaged(existing);
      if (!existingCleared) {
        nextRecoveryByRoom[roomKey] = mediaAgentRecovery;
      }
    }
    set({
      aggregateByRoom: { ...get().aggregateByRoom, [roomKey]: performControl.aggregate },
      itemsByRoom: { ...get().itemsByRoom, [roomKey]: { ...performControl.items } },
      cueStateByRoom: nextCueStateByRoom,
      cueSheetByRoom: nextCueSheetByRoom,
      mediaAgentRecoveryByRoom: nextRecoveryByRoom,
    });
    seedLiveSlideStoreFromMediaSequenceIndex(mediaSequenceIndex);
  },
  replaceCueStateFromGet: (roomKey, cueState) => {
    const lastSeq = get().lastSeqByRoom[roomKey] ?? 0;
    set({
      cueStateByRoom: { ...get().cueStateByRoom, [roomKey]: cueState },
      cueGetAuthorityUntilSeqByRoom: {
        ...get().cueGetAuthorityUntilSeqByRoom,
        [roomKey]: lastSeq,
      },
    });
  },
  resetRoom: (roomKey) => {
    set((s) => {
      const lastSeqByRoom = { ...s.lastSeqByRoom };
      delete lastSeqByRoom[roomKey];
      const aggregateByRoom = { ...s.aggregateByRoom };
      delete aggregateByRoom[roomKey];
      const itemsByRoom = { ...s.itemsByRoom };
      delete itemsByRoom[roomKey];
      const cueStateByRoom = { ...s.cueStateByRoom };
      delete cueStateByRoom[roomKey];
      const cueGetAuthorityUntilSeqByRoom = { ...s.cueGetAuthorityUntilSeqByRoom };
      delete cueGetAuthorityUntilSeqByRoom[roomKey];
      const mediaAgentRecoveryByRoom = { ...s.mediaAgentRecoveryByRoom };
      delete mediaAgentRecoveryByRoom[roomKey];
      const cueSheetByRoom = { ...s.cueSheetByRoom };
      delete cueSheetByRoom[roomKey];
      return {
        lastSeqByRoom,
        aggregateByRoom,
        itemsByRoom,
        cueStateByRoom,
        cueGetAuthorityUntilSeqByRoom,
        mediaAgentRecoveryByRoom,
        cueSheetByRoom,
      };
    });
  },
}));

function seedLiveSlideStoreFromMediaSequenceIndex(mediaSequenceIndex: Record<string, number> | undefined): void {
  if (!mediaSequenceIndex) {
    return;
  }
  const liveSlideStore = useLiveSequenceIndexStore.getState();
  const localPreviewStore = useLocalMediaPreviewSlideStore.getState();
  for (const [mediaIdRaw, slideIndex] of Object.entries(mediaSequenceIndex)) {
    if (!(slideIndex > 0) || !mediaIdRaw) {
      continue;
    }
    const itemId = mediaIdRaw.startsWith('media:') ? mediaIdRaw : `media:${mediaIdRaw}`;
    const bareMediaId = itemId.slice('media:'.length);
    if (liveSlideStore.isNativePending(itemId)) {
      liveSlideStore.setIndex(itemId, slideIndex, { confirm: true });
      continue;
    }
    if (liveSlideStore.isRecentlyOptimistic(itemId)) {
      continue;
    }
    // Next/item preview operator pick must win over a stale durable echo until take.
    if (localPreviewStore.isRecentlyOperatorPicked(bareMediaId)) {
      continue;
    }
    const current = liveSlideStore.indexByItemId[itemId];
    // A step window from this subscription is the deck on air now. A saved resume index lags
    // an auto-loop and must not pull Current playout back. A window from a dropped socket or
    // a Control visit that already unmounted does not block a newer resume index.
    const liveTiming = liveSlideStore.stepTimingByItemId[itemId];
    if (liveSlideStore.isActiveStepTiming(itemId) && current === liveTiming?.index) {
      continue;
    }
    // Durable map is authoritative for resume; allow upgrades and corrections.
    if (typeof current === 'number' && current === slideIndex) {
      continue;
    }
    liveSlideStore.setIndex(itemId, slideIndex);
  }
}

/** Non-React entry for `PlayerSubscriptionManager` (socket handler). */
export function applyPerformControlWsPayload(roomKey: EntityRoomKey, payload: LiveContextPerformControlWsPayloadV1) {
  const applied = usePerformControlWsStore.getState().applyPayload(roomKey, payload);
  if (applied) {
    seedLiveSlideStoreFromMediaSequenceIndex(payload.mediaSequenceIndex);
  }
}

export function hydratePerformControlFromGet(
  roomKey: EntityRoomKey,
  performControl: PerformControlV1,
  cueState?: FlowCueStateV1,
  cueSheet?: LiveCueSheetV1,
  mediaSequenceIndex?: Record<string, number>,
  mediaAgentRecovery?: MediaAgentRecoveryV1,
) {
  usePerformControlWsStore
    .getState()
    .hydrateFromGet(roomKey, performControl, cueState, cueSheet, mediaSequenceIndex, mediaAgentRecovery);
}

const performControlRoomRefCount = new Map<string, number>();

/** Reset the room only when the last Control/Perform owner unmounts. */
export function acquirePerformControlRoom(roomKey: EntityRoomKey): () => void {
  performControlRoomRefCount.set(roomKey, (performControlRoomRefCount.get(roomKey) ?? 0) + 1);
  return () => {
    const next = (performControlRoomRefCount.get(roomKey) ?? 1) - 1;
    if (next <= 0) {
      performControlRoomRefCount.delete(roomKey);
      resetPerformControlRoom(roomKey);
      return;
    }
    performControlRoomRefCount.set(roomKey, next);
  };
}

/** Post-cue-command GET — replace cue overlay; see store `replaceCueStateFromGet`. */
export function replaceCueStateFromGet(roomKey: EntityRoomKey, cueState: FlowCueStateV1) {
  usePerformControlWsStore.getState().replaceCueStateFromGet(roomKey, cueState);
}

export function resetPerformControlRoom(roomKey: EntityRoomKey) {
  usePerformControlWsStore.getState().resetRoom(roomKey);
  useLiveSequenceIndexStore.getState().invalidatePlayoutTiming();
}
